// Detailed, per-OS remediation steps for the 5 device compliance checks the
// Rego WatchTower agent evaluates:
//   disk_encryption_enabled, firewall_enabled, screen_timeout_enabled,
//   auto_update_enabled, antivirus_installed
//
// Each check entry has:
//   id, title, icon (emoji), why (string), steps (array of { heading, items[],
//   image?, imageAlt?, imageCaption? }), commands (optional array of
//   { label, code }), verify (optional string).
// Content is intentionally exhaustive so an employee can self-remediate without help.
//
// Screenshots live in src/Assets/Images and are required() so the bundler
// fingerprints them. Wrapped in a helper because a missing file would otherwise
// throw at module load and take the whole page down — a step with no picture is
// a much smaller problem than a blank remediation page.
const stepImage = (fileName) => {
  try {
    // eslint-disable-next-line global-require, import/no-dynamic-require
    return require(`../../Assets/Images/${fileName}`);
  } catch {
    return null;
  }
};

const macFirewallImage = stepImage("mac-firewall-settings.png");
const macAutoUpdateImage = stepImage("mac-automatic-updates.png");

export const OS_LIST = [
  { key: "windows", label: "Windows", icon: "🪟" },
  { key: "mac", label: "macOS", icon: "🍎" },
  { key: "linux", label: "Linux", icon: "🐧" },
];

const remediationData = {
  windows: [
    {
      id: "disk_encryption_enabled",
      title: "Disk Encryption (BitLocker)",
      icon: "🔒",
      why: "Full-disk encryption protects the data on your machine if it is ever lost or stolen. Without it, anyone who removes the drive can read your files. BitLocker is the built-in encryption for Windows Pro/Enterprise/Education.",
      steps: [
        {
          heading: "Turn on BitLocker (GUI)",
          items: [
            "Press the Start button and type \"BitLocker\", then open \"Manage BitLocker\".",
            "Next to your operating-system drive (usually C:), click \"Turn on BitLocker\".",
            "When prompted, choose how to back up your recovery key. Save it to your Microsoft account or a secure location — you will need it if the drive ever locks.",
            "Choose \"Encrypt entire drive\" (more secure) rather than \"used disk space only\" for existing machines.",
            "Select \"New encryption mode (XTS-AES)\" and click \"Start encrypting\".",
            "Leave the machine plugged in and let encryption finish. It can run in the background while you work.",
          ],
        },
        {
          heading: "If BitLocker says a TPM is required",
          items: [
            "Restart and enter your BIOS/UEFI (usually F2, F10, or Del during boot).",
            "Find the Security section and enable the TPM / \"Security Device\" / \"PTT\" (Intel) or \"fTPM\" (AMD).",
            "Save and exit, then retry the steps above.",
          ],
        },
      ],
      commands: [
        { label: "Check status", code: "manage-bde -status C:" },
        {
          label: "Enable from an elevated PowerShell / CMD",
          code: "manage-bde -on C: -RecoveryPassword",
        },
      ],
      verify:
        'Run "manage-bde -status C:" — you should see "Protection Status: Protection On" and "Percentage Encrypted: 100%".',
    },
    {
      id: "firewall_enabled",
      title: "Firewall (Windows Defender Firewall)",
      icon: "🧱",
      why: "The firewall blocks unsolicited inbound network connections, which is a first line of defence against malware and remote attacks. It must be on for every network profile (Domain, Private, Public).",
      steps: [
        {
          heading: "Turn the firewall on (GUI)",
          items: [
            "Open Start, type \"Windows Security\", and open it.",
            "Go to \"Firewall & network protection\".",
            "For each profile — Domain network, Private network, and Public network — click it and switch \"Microsoft Defender Firewall\" to On.",
            "Do not add broad \"Allow an app\" exceptions unless your IT team instructs you to.",
          ],
        },
      ],
      commands: [
        {
          label: "Enable all profiles (elevated CMD)",
          code: "netsh advfirewall set allprofiles state on",
        },
      ],
      verify:
        'Run "netsh advfirewall show allprofiles state" — every profile should report "State ON".',
    },
    {
      id: "screen_timeout_enabled",
      title: "Screen Lock & Timeout",
      icon: "⏱️",
      why: "An automatic lock ensures that if you step away, no one can use your unlocked session. Policy requires the screen to lock after 15 minutes or less of inactivity and to require a password to get back in.",
      steps: [
        {
          heading: "Lock via the screen saver (this on its own satisfies the check)",
          items: [
            'Press Win+R, type "control desk.cpl,,@screensaver" and press Enter.',
            'Pick any screen saver other than "(None)" — the lock will not arm if none is selected.',
            'Set "Wait" to 15 minutes or less.',
            'Tick "On resume, display logon screen", then click Apply.',
          ],
        },
        {
          heading: "Alternative — only if your organisation disables screen savers",
          items: [
            "Open Settings → Accounts → Sign-in options.",
            'Under "If you\'ve been away, when should Windows require you to sign in again?", choose "When PC wakes up from sleep".',
            'Open Settings → System → Power & battery → Screen and sleep, and set "Sleep" (not "Screen") to 15 minutes or less.',
            "Set it on both battery and plugged in — the longer of the two is what counts.",
          ],
        },
        {
          // Its own group rather than extra numbered items on the one above: this
          // is the same fix on a differently-named pane, so a reader follows one
          // list or the other, not both.
          heading: "Or, on newer Windows 11 builds",
          items: [
            'Open Settings → System → Power & battery → "Screen, sleep, & hibernate timeouts" (this pane is called "Screen and sleep" on older builds).',
            'Set all configs in both the section : "On battery power" and "When plugged in" to under 15 minutes.',
          ],
        },
      ],
      verify:
        'Confirm the screen saver "Wait" shows 15 minutes or less with "On resume, display logon screen" ticked — or, if you used the alternative, that "Sleep" is 15 minutes or less on both battery and plugged in.',
    },
    {
      id: "auto_update_enabled",
      title: "Automatic Updates (Windows Update)",
      icon: "🔄",
      why: "Security patches close vulnerabilities that attackers actively exploit. Windows Update must be able to download and install updates automatically so your machine stays patched.",
      steps: [
        {
          heading: "Start the Windows Update service (requires local administrator rights)",
          items: [
            "Press Win + R, type services.msc, and press Enter.",
            "In the Services list, scroll to Windows Update and double-click it to open its properties.",
            "On the General tab, set Startup type to Automatic.",
            "If Service status reads Stopped, click Start and wait for it to report Running.",
            "Click Apply, then OK, and close the Services window.",
            "Your device will report the updated status on its next agent sync.",
          ],
        },
      ],
      commands: [
        {
          label: "Ensure the update service starts automatically (elevated)",
          code: "sc config wuauserv start= auto\nnet start wuauserv",
        },
      ],
      verify:
        "In Settings → Windows Update, the status should read \"You're up to date\" and updates should not be paused.",
    },
    {
      id: "antivirus_installed",
      title: "Antivirus (Microsoft Defender)",
      icon: "🛡️",
      why: "Antivirus with real-time protection detects and blocks malware before it can run. Microsoft Defender is built into Windows; if your organization uses a third-party product, that one must be running and up to date instead.",
      steps: [
        {
          heading: "Confirm Defender real-time protection is on",
          items: [
            "Open Windows Security → Virus & threat protection.",
            'Under "Virus & threat protection settings" click "Manage settings" and turn on "Real-time protection".',
            'Click "Check for updates" under "Virus & threat protection updates" to pull the latest definitions.',
          ],
        },
        {
          heading: "If you use a third-party antivirus",
          items: [
            "Make sure the product (e.g. CrowdStrike, SentinelOne, Sophos) is installed, running, and shows a green/healthy status.",
            "Confirm its definitions/agent version are current.",
          ],
        },
      ],
      commands: [
        {
          label: "Check Defender status (PowerShell)",
          code: "Get-MpComputerStatus | Select AntivirusEnabled, RealTimeProtectionEnabled, AntivirusSignatureLastUpdated",
        },
      ],
      verify:
        "Get-MpComputerStatus should show AntivirusEnabled: True and RealTimeProtectionEnabled: True (or your third-party AV reports healthy).",
    },
  ],

  mac: [
    {
      id: "disk_encryption_enabled",
      title: "Disk Encryption (FileVault)",
      icon: "🔒",
      why: "FileVault encrypts your entire startup disk so your data is unreadable without your login password. It protects company data if the Mac is lost or stolen.",
      steps: [
        {
          heading: "Turn on FileVault (GUI)",
          items: [
            "Open System Settings → Privacy & Security → FileVault (on older macOS: System Preferences → Security & Privacy → FileVault).",
            'Click "Turn On…".',
            "Choose how to unlock the disk and reset your password: use your iCloud account, or create a recovery key. If you create a recovery key, store it somewhere safe — losing it can mean losing your data.",
            "Let encryption complete. It runs in the background; keep the Mac plugged in.",
          ],
        },
      ],
      commands: [
        { label: "Check status", code: "fdesetup status" },
        { label: "Enable from Terminal", code: "sudo fdesetup enable" },
      ],
      verify:
        'Run "fdesetup status" — it should print "FileVault is On."',
    },
    {
      id: "firewall_enabled",
      title: "Firewall (Application Firewall)",
      icon: "🧱",
      why: "The macOS application firewall controls incoming connections to your Mac, blocking unsolicited inbound traffic to apps and services that should not be exposed.",
      steps: [
        {
          heading: "Turn on the firewall (GUI)",
          items: [
            "Open System Settings (Apple menu  → System Settings).",
            'Type "firewall" into the search box at the top left, then choose "Turn firewall on or off".',
            "Switch the Firewall toggle on — it turns blue when it is on.",
            'When it is on, the description underneath reads "The firewall is turned on and set up to prevent unauthorised applications, programs and services from accepting incoming connections."',
            'Optionally click "Options…" and enable "Enable stealth mode" so your Mac does not respond to probes.',
          ],
          image: macFirewallImage,
          imageAlt: "macOS System Settings showing the Firewall toggle switched on",
          imageCaption:
            "The Firewall toggle on the right should be blue. If it is grey, click it once.",
        },
      ],
      commands: [
        {
          label: "Enable from Terminal",
          code: "sudo /usr/libexec/ApplicationFirewall/socketfilterfw --setglobalstate on",
        },
        {
          label: "Check state",
          code: "/usr/libexec/ApplicationFirewall/socketfilterfw --getglobalstate",
        },
      ],
      verify:
        'The command above should report "Firewall is enabled. (State = 1)".',
    },
    {
      id: "screen_timeout_enabled",
      title: "Screen Lock & Timeout",
      icon: "⏱️",
      why: "Requiring a password once the screen saver or display sleep begins ensures an unattended Mac locks itself, protecting your session when you walk away. Policy requires the Mac to lock after 15 minutes or less of inactivity.",
      steps: [
        {
          heading: "Require a password immediately",
          items: [
            "Open System Settings → Lock Screen.",
            'Set "Require password after screen saver begins or display is turned off" to "Immediately".',
            "This delay is added on top of your inactivity time, so anything above Immediately pushes a 15-minute setting over the limit.",
          ],
        },
        {
          heading: "Set the inactivity timeout",
          items: [
            'In the same Lock Screen pane, set "Start Screen Saver when inactive" to 15 minutes or less.',
            'Set "Turn display off on battery when inactive" and "…on power adapter" to 15 minutes or less.',
            "Both power sources are read, and the longer of the two is what counts.",
          ],
        },
      ],
      verify:
        'Run "sysadminctl -screenLock status" — the grace period should be 0, and the screen saver idle time should read 900 seconds or less.',
    },
    {
      id: "auto_update_enabled",
      title: "Automatic Updates (Software Update)",
      icon: "🔄",
      why: "Apple ships frequent security fixes. Automatic updates keep macOS and its security responses current without you having to remember to check.",
      steps: [
        {
          heading: "Enable automatic updates (GUI)",
          items: [
            "Open System Settings (Apple menu  → System Settings).",
            "In the sidebar choose General, then Software Update.",
            'Click the ⓘ button next to "Automatic updates" to open the panel shown below.',
            'Switch all three on: "Download new updates when available", "Install macOS updates", and "Install system data files and security updates".',
            'Click "Done". Every toggle must be on — leaving any one off will keep this check failing.',
          ],
          image: macAutoUpdateImage,
          imageAlt:
            "macOS Automatic updates panel with the download and install toggles",
          imageCaption:
            "All three toggles are off in this example — switch each one on, then click Done.",
        },
        {
          heading: "Or check and fix it from Terminal",
          items: [
            "Open Terminal (Applications → Utilities → Terminal).",
            'Run the four "check" commands below. Each one prints the current setting.',
            '1 means the setting is on. 0, a blank line, or a "does not exist" error all mean it is off.',
            "If any of them is not 1, run the four commands in the second block to turn them all on. They need sudo, so you will be asked for your password.",
            "Re-run the check commands afterwards — all four should now print 1.",
          ],
        },
      ],
      commands: [
        {
          label: "Check — each should print 1",
          code: [
            "defaults read /Library/Preferences/com.apple.SoftwareUpdate AutomaticDownload",
            "defaults read /Library/Preferences/com.apple.SoftwareUpdate AutomaticallyInstallMacOSUpdates",
            "defaults read /Library/Preferences/com.apple.SoftwareUpdate ConfigDataInstall",
            "defaults read /Library/Preferences/com.apple.SoftwareUpdate CriticalUpdateInstall",
          ].join("\n"),
        },
        {
          label: "Fix — turn all four on",
          code: [
            "sudo defaults write /Library/Preferences/com.apple.SoftwareUpdate AutomaticDownload -bool true",
            "sudo defaults write /Library/Preferences/com.apple.SoftwareUpdate AutomaticallyInstallMacOSUpdates -bool true",
            "sudo defaults write /Library/Preferences/com.apple.SoftwareUpdate ConfigDataInstall -bool true",
            "sudo defaults write /Library/Preferences/com.apple.SoftwareUpdate CriticalUpdateInstall -bool true",
          ].join("\n"),
        },
        {
          label: "Enable scheduled checks from Terminal",
          code: "sudo softwareupdate --schedule on",
        },
      ],
      verify:
        'Re-run the four "defaults read" commands — every one should print 1. In System Settings → General → Software Update, the toggles behind the ⓘ next to "Automatic updates" should all be on.',
    },
    {
      id: "antivirus_installed",
      title: "Antivirus / Malware Protection (XProtect)",
      icon: "🛡️",
      why: "XProtect is Apple's built-in malware protection, and it is what this check reads. It is always on and cannot be switched off — what matters is that its signatures are present and current, since an outdated copy cannot recognise recent malware. Apple ships those updates silently through the system data & security updates channel.",
      steps: [
        {
          heading: "1. Confirm XProtect scanning is on",
          items: [
            "Open Terminal (Applications → Utilities → Terminal).",
            "Run xprotect status. Both lines must read enabled.",
            "If either reads disabled, a configuration profile is restricting XProtect — check System Settings → General → Device Management and contact your IT team.",
            // The agent reads the bundles directly precisely because this CLI is
            // missing on some macOS builds, so send people there rather than
            // leaving them stuck on "command not found".
            'If Terminal answers "command not found", your macOS build has no xprotect command — use the "Fallback" command below instead, which reads the same versions this check reads.',
          ],
        },
        {
          heading: "2. Confirm signatures are current",
          items: [
            "Run xprotect version. It prints the signature version and install date.",
            "If the date is within the last few weeks, you're done — the check clears on your next agent sync.",
          ],
        },
        {
          heading: "3. If the date is old, refresh them",
          items: [
            'Open System Settings → General → Software Update → ⓘ next to "Automatic updates".',
            'Turn on "Install Security Responses and system files" (some macOS versions label this "Install system data files and security updates"). This is the channel XProtect signatures arrive through.',
            "Then run sudo xprotect update, wait a minute, and re-run xprotect version. The install date should show today.",
          ],
        },
      ],
      commands: [
        { label: "Check scanning is enabled", code: "xprotect status" },
        { label: "Check signature version and install date", code: "xprotect version" },
        { label: "Refresh signatures now", code: "sudo xprotect update" },
        {
          // Same two values the agent itself reads, for builds without the CLI.
          label: "Fallback if xprotect is not found — read the versions directly",
          code: [
            "defaults read /Library/Apple/System/Library/CoreServices/XProtect.bundle/Contents/Info.plist CFBundleShortVersionString",
            "defaults read /Library/Apple/System/Library/CoreServices/XProtect.app/Contents/Info.plist CFBundleShortVersionString",
          ].join("\n"),
        },
      ],
      verify:
        'Run "xprotect version" — the install date should be recent (today, if you just refreshed). On builds without the xprotect command, both fallback commands should print a version number instead of an empty result.',
    },
  ],

  linux: [
    {
      id: "disk_encryption_enabled",
      title: "Disk Encryption (LUKS)",
      icon: "🔒",
      why: "LUKS full-disk encryption keeps your data unreadable without the passphrase if the machine is lost or stolen. It is normally chosen during OS installation.",
      steps: [
        {
          heading: "Check whether the disk is already encrypted",
          items: [
            'Open a terminal and run "lsblk" — an encrypted setup shows a "crypt" type layer over your partition.',
            "If you see LUKS/crypt entries, encryption is already active and you only need to keep your passphrase safe.",
          ],
        },
        {
          heading: "If the disk is NOT encrypted",
          items: [
            "Whole-disk LUKS cannot be enabled in place safely — back up your data first.",
            "Reinstall your distribution and choose the \"Encrypt the new installation\" / \"Use LVM with encryption\" option during the installer's disk step.",
            "Alternatively, if only a secondary data disk needs encrypting, create a LUKS container on it (commands below) and migrate data there.",
          ],
        },
      ],
      commands: [
        { label: "Inspect block devices", code: "lsblk -f" },
        {
          label: "Create + open a LUKS container on a spare device (DATA IS ERASED)",
          code: "sudo cryptsetup luksFormat /dev/sdX\nsudo cryptsetup open /dev/sdX secure\nsudo mkfs.ext4 /dev/mapper/secure",
        },
      ],
      verify:
        'Run "sudo cryptsetup status <name>" or check "lsblk -f" — the target device should show type "crypt" / a LUKS UUID.',
    },
    {
      id: "firewall_enabled",
      title: "Firewall (ufw / firewalld)",
      icon: "🧱",
      why: "A host firewall blocks unsolicited inbound connections. Debian/Ubuntu commonly use ufw; Fedora/RHEL use firewalld.",
      steps: [
        {
          heading: "Ubuntu / Debian (ufw)",
          items: [
            "Install if needed and enable ufw with a default-deny inbound policy.",
            "Only open ports you actually need (e.g. SSH) explicitly.",
          ],
        },
        {
          heading: "Fedora / RHEL / CentOS (firewalld)",
          items: [
            "Enable and start the firewalld service so it runs at boot.",
          ],
        },
      ],
      commands: [
        {
          label: "ufw (Debian/Ubuntu)",
          code: "sudo apt install ufw\nsudo ufw default deny incoming\nsudo ufw enable\nsudo ufw status verbose",
        },
        {
          label: "firewalld (Fedora/RHEL)",
          code: "sudo systemctl enable --now firewalld\nsudo firewall-cmd --state",
        },
      ],
      verify:
        'ufw: "sudo ufw status" reports "Status: active". firewalld: "sudo firewall-cmd --state" reports "running".',
    },
    {
      id: "screen_timeout_enabled",
      title: "Screen Lock & Timeout",
      icon: "⏱️",
      why: "An automatic lock protects your session when you step away. On GNOME desktops this is controlled by the screen-blank delay and automatic screen lock. Policy requires the screen to lock after 15 minutes or less of inactivity.",
      steps: [
        {
          heading: "GNOME (Ubuntu, Fedora Workstation)",
          items: [
            "Open Settings → Privacy → Screen (or Settings → Privacy & Security → Screen Lock).",
            'Turn on "Automatic Screen Lock".',
            'Set "Blank Screen Delay" to 15 minutes or less.',
            'Set "Automatic Screen Lock Delay" to "Screen Turns Off" — this delay is added on top of the blank delay, so anything longer pushes a 15-minute setting over the limit.',
            'Optionally set "Lock Screen on Suspend" on.',
          ],
        },
      ],
      commands: [
        {
          label: "Set via gsettings (GNOME)",
          code: "gsettings set org.gnome.desktop.session idle-delay 900\ngsettings set org.gnome.desktop.screensaver lock-enabled true\ngsettings set org.gnome.desktop.screensaver lock-delay 0",
        },
      ],
      verify:
        "Leave the desktop idle past the delay — the screen should blank and require your password to unlock.",
    },
    {
      id: "auto_update_enabled",
      title: "Automatic Updates",
      icon: "🔄",
      why: "Automatically applying security updates keeps known vulnerabilities patched. Debian/Ubuntu use unattended-upgrades; Fedora/RHEL use dnf-automatic.",
      steps: [
        {
          heading: "Ubuntu / Debian (unattended-upgrades)",
          items: [
            "Install the unattended-upgrades package.",
            "Enable it so security updates install automatically.",
          ],
        },
        {
          heading: "Fedora / RHEL (dnf-automatic)",
          items: [
            "Install dnf-automatic and enable its timer.",
          ],
        },
      ],
      commands: [
        {
          label: "unattended-upgrades (Debian/Ubuntu)",
          code: "sudo apt install unattended-upgrades\nsudo dpkg-reconfigure --priority=low unattended-upgrades",
        },
        {
          label: "dnf-automatic (Fedora/RHEL)",
          code: "sudo dnf install dnf-automatic\nsudo systemctl enable --now dnf-automatic.timer",
        },
      ],
      verify:
        'unattended-upgrades: "systemctl status unattended-upgrades" is active. dnf-automatic: "systemctl status dnf-automatic.timer" is active/waiting.',
    },
    {
      id: "antivirus_installed",
      title: "Antivirus (ClamAV)",
      icon: "🛡️",
      why: "Endpoint malware scanning is expected on managed Linux devices. ClamAV is the common open-source option; your organization may require a specific agent instead.",
      steps: [
        {
          heading: "Install and enable ClamAV",
          items: [
            "Install the clamav and clamav-daemon packages.",
            "Enable freshclam so virus definitions update automatically.",
            "Enable the clamav daemon so on-access/scheduled scanning can run.",
          ],
        },
        {
          heading: "If your organization requires a specific agent",
          items: [
            "Install the required endpoint agent (e.g. CrowdStrike Falcon) from your IT team's package repository.",
            "Confirm its service is enabled and reporting healthy.",
          ],
        },
      ],
      commands: [
        {
          label: "Install ClamAV (Debian/Ubuntu)",
          code: "sudo apt install clamav clamav-daemon\nsudo systemctl enable --now clamav-freshclam\nsudo systemctl enable --now clamav-daemon",
        },
        { label: "Run an on-demand scan", code: "clamscan -r --bell -i ~" },
      ],
      verify:
        '"systemctl status clamav-daemon" is active (running) and "freshclam" reports an up-to-date database.',
    },
  ],
};

export default remediationData;
